// What a relationship group says about itself, in one place.
//
// The explorer opens an object into one group per relationship **and
// direction** (6 Oct 2026). It grouped by the type of thing at the other end
// until then, which under a strict taxonomy is the same answer - where a child
// of one type can only hang off its parent one way, the type names the
// relationship and nobody notices the difference. Where the same two types are
// joined several ways it is not: an object that starts at one thing and ends
// at another had both in one chip named after their shared type, and a third
// relationship, running the other way, in a chip that said only what it
// pointed at. The relationship is what the reader is following, so it is what
// the group stands for; each object still wears its own type on its card.
//
// Pure: no React, no DOM, no network. The chip, the hover card and the details
// panel all read from here, so the three can't say different things about the
// same edge, and the words match the structure map's - which has grouped by
// relationship and direction all along.
import { attributeLabel, displayValue, relationshipLabel } from "../../utils/viewConfig";

// At a glance, in the reading direction. An edge that runs out of the object
// the reader is standing on points right; one pointing at it points left.
export const directionMark = (direction) => (direction === "in" ? "←" : "→");

// And in words wherever there is room for them, because a reader shouldn't
// have to know what an arrow means in a graph - the same choice the structure
// map made when it wrote "pointing here" rather than drawing an arrowhead.
export const directionWords = (direction) => (direction === "in" ? "pointing at this object" : "from this object");

// A relationship with no direction recorded reads as outward: a group always
// has a way round, and "out" is the way every import writes its links.
export const groupTitle = (config, relationship, direction) =>
  `${relationshipLabel(config, relationship)}, ${directionWords(direction)}`;

// Where this relationship starts and where it ends, said as a sentence.
//
// Always written left to right in the direction the edge actually runs, so an
// inbound one names the other object first. An arrow that pointed the wrong
// way would be worse than no arrow at all.
export const connectionSentence = (config, { relationship, direction, here, there } = {}) => {
  const from = String(here || "").trim();
  const to = String(there || "").trim();
  if (!from || !to) return "";
  const label = relationshipLabel(config, relationship);
  const [left, right] = direction === "in" ? [to, from] : [from, to];
  return `${left} → ${label} → ${right}`;
};

// What a relationship carries, said in the reader's words.
//
// A link can hold attributes of its own - a library may record how an edge was
// arrived at, or how sure of it somebody is - and **which of them a reader
// sees is the admin's decision** (Display -> Settings), so nothing here knows
// a single attribute name: the names arrive with the answer and are read with
// `attributeLabel`, exactly as an object's attributes are.
//
// Three names is as many as reads at a glance; the rest are counted, because
// "and 9 more" is information and a wrapped list of twelve is not.
const NAMES_SHOWN = 3;

// A label reads as a sentence's first word here, not as a heading, so the one
// capital `attributeLabel` adds is given back.
const inline = (key) => {
  const label = attributeLabel(key);
  return label ? label.charAt(0).toLowerCase() + label.slice(1) : key;
};

const andList = (words) => {
  if (words.length <= 1) return words[0] || "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
};

export const carriesNote = (keys) => {
  const names = (keys || []).filter(Boolean);
  if (!names.length) return "";
  const shown = names.slice(0, NAMES_SHOWN).map(inline);
  const left = names.length - shown.length;
  return `Carries ${andList(left ? [...shown, `${left} more`] : shown)}`;
};

// What one edge actually holds, ready to draw: the attribute's own name read
// as a label, its value read the way every other value in the product is.
// **Nothing empty is ever a row** - the same rule the details panel follows
// for an object, so an edge with one filled attribute shows one.
export const attributeRows = (attributes) =>
  (attributes || [])
    .map((row) => ({ key: row.key, label: attributeLabel(row.key), value: displayValue(row.value) }))
    .filter((row) => row.key && row.value !== "");
